import React from "react";
import { FilterFilled, CheckCircleOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { BatchItem, BatchPackagingItem, PackagingItem } from "shared/api";
import { TableButton, TableSearchFilter } from "shared/ui";
import { Select, Spin } from "antd";
import classNames from "classnames";
import { adaptPackagesForMultipleExecuteReport } from "shared/adapters";

interface UseGetBatchesTableColumnsProps {
  t: TFunction;
  showEditModal: (batch: BatchItem) => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  batchCodeFilterValue: string;
  handleBatchCodeFilterChange: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearBatchCodeFilter: () => void;
  nameFilterValue: string;
  handleNameFilterChange: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  clearNameFilter: () => void;
  descriptionFilterValue: string;
  handleDescriptionFilterChange: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  clearDescriptionFilter: () => void;
  selectedCompanies: number[];
  adaptedCompanies: { label: string; value: number }[];
  handleCompaniesFilterChange: (values: number[]) => void;
  clearCompaniesFilter: () => void;
  isLoadingCompanies?: boolean;
  formatOrdersForDisplay: (batch: BatchItem) => string;
  handleGenerateReport: (batch: BatchItem) => void;
  showDeleteModal: (batch: BatchItem) => void;
  hasOrders: (batch: BatchItem) => boolean;
  selectedOrders: number[];
  adaptedOrders: { label: string; value: number }[];
  handleOrdersFilterChange: (values: number[]) => void;
  clearOrdersFilter: () => void;
  isLoadingOrders?: boolean;
  packagingsFilterValue: string;
  handlePackagingsFilterChange: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  clearPackagingsFilter: () => void;
  isFortnoxLoggedIn?: boolean;
  handleCreateFortnoxInvoice?: (batch: BatchItem) => void;
}

export const useGetBatchesTableColumns = ({
  t,
  showEditModal,
  hasEditPermission,
  hasDeletePermission,
  batchCodeFilterValue,
  handleBatchCodeFilterChange,
  clearBatchCodeFilter,
  nameFilterValue,
  handleNameFilterChange,
  clearNameFilter,
  descriptionFilterValue,
  handleDescriptionFilterChange,
  clearDescriptionFilter,
  selectedCompanies,
  adaptedCompanies,
  handleCompaniesFilterChange,
  clearCompaniesFilter,
  isLoadingCompanies,
  formatOrdersForDisplay,
  handleGenerateReport,
  showDeleteModal,
  hasOrders,
  selectedOrders,
  adaptedOrders,
  handleOrdersFilterChange,
  clearOrdersFilter,
  isLoadingOrders,
  packagingsFilterValue,
  handlePackagingsFilterChange,
  clearPackagingsFilter,
  isFortnoxLoggedIn,
  handleCreateFortnoxInvoice,
}: UseGetBatchesTableColumnsProps): ColumnsType<BatchItem> => [
  {
    title: t("batchesTableColumns.batchCode"),
    dataIndex: "batch_code",
    key: "batch_code",
    width: 130,
    filterIcon: (
      <FilterFilled
        className={
          batchCodeFilterValue.length ? "!text-blue-600 scale-125" : undefined
        }
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearBatchCodeFilter}
        confirm={confirm}
        value={batchCodeFilterValue}
        handleFilterChange={handleBatchCodeFilterChange}
        visible={visible}
        placeholder="Enter name"
        title={t("batchesTableColumns.name") as string}
      />
    ),
  },
  {
    title: t("batchesTableColumns.name"),
    dataIndex: "name",
    key: "name",
    filterIcon: (
      <FilterFilled
        className={
          nameFilterValue.length ? "!text-blue-600 scale-125" : undefined
        }
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearNameFilter}
        confirm={confirm}
        value={nameFilterValue}
        handleFilterChange={handleNameFilterChange}
        visible={visible}
        placeholder="Enter name"
        title={t("batchesTableColumns.name") as string}
      />
    ),
  },
  {
    title: t("batchesTableColumns.description"),
    dataIndex: "description",
    key: "description",
    render: (description: string) => description || "-",
    filterIcon: (
      <FilterFilled
        className={
          descriptionFilterValue.length ? "!text-blue-600 scale-125" : undefined
        }
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearDescriptionFilter}
        confirm={confirm}
        value={descriptionFilterValue}
        handleFilterChange={handleDescriptionFilterChange}
        visible={visible}
        placeholder="Enter description"
        title={t("batchesTableColumns.description") as string}
      />
    ),
  },
  {
    title: t("batchesTableColumns.company"),
    dataIndex: "company",
    key: "company",
    render: (company) => (typeof company === "object" ? company?.name : "—"),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": selectedCompanies.length > 0,
        })}
      />
    ),
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8, width: 300 }}>
        {isLoadingCompanies ? (
          <div className="flex items-center justify-center py-4">
            <Spin size="small" />
            <span className="ml-2 text-sm text-gray-500">
              Loading companies...
            </span>
          </div>
        ) : (
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder="Select companies"
            options={adaptedCompanies}
            value={selectedCompanies}
            onChange={(values) => {
              handleCompaniesFilterChange(values);
              confirm();
            }}
            onClear={() => {
              clearCompaniesFilter();
              confirm();
            }}
            style={{ width: "100%" }}
            filterOption={(input, option) =>
              (option?.label?.toString() ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        )}
      </div>
    ),
  },
  {
    title: t("batchesTableColumns.orders"),
    key: "orders",
    render: (_, record) => formatOrdersForDisplay(record),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": selectedOrders.length > 0,
        })}
      />
    ),
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8, width: 300 }}>
        {isLoadingOrders ? (
          <div className="flex items-center justify-center py-4">
            <Spin size="small" />
            <span className="ml-2 text-sm text-gray-500">
              Loading orders...
            </span>
          </div>
        ) : (
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder="Select orders"
            options={adaptedOrders}
            value={selectedOrders}
            onChange={(values) => {
              handleOrdersFilterChange(values);
              confirm();
            }}
            onClear={() => {
              clearOrdersFilter();
              confirm();
            }}
            style={{ width: "100%" }}
            filterOption={(input, option) =>
              (option?.label?.toString() ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        )}
      </div>
    ),
  },
  {
    title: t("batchesTableColumns.packagings"),
    key: "packagings",
    render: (_, record: BatchItem) => {
      const rawItems =
        (record.packagings as BatchPackagingItem[])?.map((p) => {
          const pkg = p.packaging;
          return {
            id: pkg.id,
            name: pkg.name,
            order: pkg.order,
            material: pkg.material,
          } as unknown as PackagingItem;
        }) || [];

      const adapted = adaptPackagesForMultipleExecuteReport(rawItems);
      return adapted.length
        ? adapted.map((item) => item.label).join(", ")
        : "—";
    },
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": packagingsFilterValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearPackagingsFilter}
        confirm={confirm}
        value={packagingsFilterValue}
        handleFilterChange={handlePackagingsFilterChange}
        visible={visible}
        placeholder="Enter packaging name"
        title={t("batchesTableColumns.packagings") as string}
      />
    ),
  },
  {
    title: "Fortnox Document",
    dataIndex: "fortnox_document_number",
    key: "fortnox_document_number",
    width: 180,
    render: (fortnox_document_number: string) => {
      if (fortnox_document_number) {
        return (
          <div className="flex items-center gap-2">
            <CheckCircleOutlined className="text-green-600 text-xl" />
            <span>{fortnox_document_number}</span>
          </div>
        );
      }
      return null;
    },
  },
  {
    title: t("ordersTableColumns.actions"),
    key: "actions",
    width: 180,
    render: (_, record) => (
      <div className="w-full flex items-center gap-2">
        {hasEditPermission && (
          <TableButton type="edit" onClick={() => showEditModal(record)} />
        )}
        {hasDeletePermission && (
          <TableButton type="delete" onClick={() => showDeleteModal(record)} />
        )}
        {hasOrders(record) && (
          <TableButton
            type="rIcon"
            onClick={() => handleGenerateReport(record)}
            popoverString="Generate Loading List Report"
          />
        )}
        {isFortnoxLoggedIn &&
          handleCreateFortnoxInvoice &&
          !record.fortnox_document_number && (
            <TableButton
              type="fortnox"
              onClick={() => handleCreateFortnoxInvoice(record)}
              popoverString="Create Fortnox Invoice"
            />
          )}
      </div>
    ),
  },
];
